/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  App,
  Col,
  Row,
  TableColumnsType,
  Tooltip,
  Button,
  InputNumber,
  Select,
  Button as ButtonAnt
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  CloseCircleOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { Table } from '@/components/tables/table';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  // getListPhysicalActivity,
  resetMessages,
  deletePhysicalActivity,
  savePhysicalActivitySlider,
  watchListPhysicalActivity,
  watchPhysicalActivityMessage,
  watchPhysicalActivityPagination,
  watchPhysicalActivityLoading
} from '@/redux/slices/regulate-level-physical-activity.slice';
import { useEffect, useState, useMemo } from 'react';
import { setMessages } from '@/redux/slices/app.slice';
import { tableScrollY } from '@/utils/common';
import Input from 'antd/es/input/Input';
import { watchListFitnessLevel } from '@/redux/slices/regulate-level-physical-activity.slice';
import usePermission from '@/hooks/permission.hook';
import { useBeforeUnload } from 'react-router-dom';

type PageChangeParams = {
  page?: number;
  pageSize?: number;
  filter?: FilterPhysicalActivity;
};

type PhysicalActivitySliderTableProps = {
  handleSearch: (values: FilterPhysicalActivity) => void;
  handlePageChange: ({ page, pageSize, filter }: PageChangeParams) => void;
  filter: FilterPhysicalActivity;
};

export const RegulateLevelPhysicalActivityTable = ({
  handleSearch,
  handlePageChange,
  filter
}: PhysicalActivitySliderTableProps) => {
  const dispatch = useAppDispatch();
  const { modal, notification } = App.useApp();
  const listPhysicalActivity = useAppSelector(watchListPhysicalActivity);
  const [displayData, setDisplayData] = useState<PhysicalActivity[]>([]);

  const messageInfo = useAppSelector(watchPhysicalActivityMessage);
  const pagination = useAppSelector(watchPhysicalActivityPagination);
  const loading = useAppSelector(watchPhysicalActivityLoading);
  const fitnessLevelList = useAppSelector(watchListFitnessLevel);

  const { creatable, updatable, deletable } = usePermission();

  const [editingRowIds, setEditingRowIds] = useState<string[]>([]);

  const [editedData, setEditedData] = useState<
    Record<string, Record<string, any>>
  >({});

  const handleEdit = (record: PhysicalActivity) => {
    setEditingRowIds((prev) => [...prev, record.id]);
    setEditedData((prev) => ({ ...prev, [record.id]: { ...record } }));
  };

  const handleInputChange = (id: string, key: string, value: any) => {
    setEditedData((prev) => ({
      ...prev,
      [id]: { ...prev[id], [key]: value }
    }));
  };

  const onAdd = () => {
    const tempId = `temp_${Date.now()}`;
    const newRow = { ...emptyRow, id: tempId };
    setDisplayData([newRow, ...listPhysicalActivity]);
    setEditingRowIds((prev) => [...prev, newRow.id]);
    setEditedData((prev) => ({ ...prev, [newRow.id]: newRow }));
  };

  const handleSave = async (id: any) => {
    const data = editedData[id];
    const requiredFields = ['name', 'ageMin', 'ageMax', 'option'];
    const isMissingField = requiredFields.some(
      (key) =>
        data[key] === null ||
        data[key] === undefined ||
        data[key] === '' ||
        (key === 'option' && (!data.option || !data.option.id))
    );

    if (isMissingField) {
      notification.error({
        message: 'Vui lòng điền đủ các trường thông tin bắt buộc!'
      });
      return;
    }

    const payload: any = {
      name: data.name,
      ageMin: data.ageMin,
      ageMax: data.ageMax,
      idOptions: data.option.id
    };
    const isTempId = typeof id === 'string' && id.startsWith('temp_');
    if (!isTempId) {
      payload.id = id;
    }

    await dispatch(savePhysicalActivitySlider(payload));
    handlePageChange({});

    setEditingRowIds((prev) => prev.filter((rowId) => rowId !== id));
  };

  const handleCancel = (id: string) => {
    setEditingRowIds((prev) => prev.filter((rowId) => rowId !== id));

    if (id === '') {
      setDisplayData((prevData) => prevData.filter((item) => item.id !== ''));
    }
  };

  const handleReset = () => {
    handlePageChange({ filter: {} });
  };

  const fitnessLevelOptions = useMemo(
    () => [
      ...fitnessLevelList.map((fitnessLevel) => ({
        value: fitnessLevel.id,
        label: fitnessLevel.name
      }))
    ],
    [fitnessLevelList]
  );

  const emptyRow: PhysicalActivity = {
    id: '',
    name: '',
    ageMin: undefined,
    ageMax: undefined,
    idOptions: undefined,
    isDeleted: false
  };

  const handlePhysicalActivity = async (physicalActivityId: number) => {
    await dispatch(deletePhysicalActivity(physicalActivityId));
    const remainingRecords = listPhysicalActivity.length;
    if (remainingRecords === 0 && pagination.curPage > 1) {
      handlePageChange({
        page: pagination.curPage,
        pageSize: pagination.limitPage,
        filter
      });
    } else {
      handlePageChange({
        page: pagination.curPage,
        pageSize: pagination.limitPage,
        filter
      });
    }
  };

  useEffect(() => {
    setDisplayData((prevData) => {
      const newRows = prevData.filter((item) => item.id === '');
      return [...newRows, ...listPhysicalActivity];
    });
  }, [listPhysicalActivity]);

  const confirmDeleteAdmin = (physicalActivityId: number) => {
    modal.confirm({
      title: 'Bạn có chắc chắn xoá mức độ hoạt động thể lực này không?',
      content: <em>Sau khi xoá sẽ không thể hoàn tác</em>,
      okText: 'Xác nhận',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => handlePhysicalActivity(physicalActivityId)
    });
  };

  const columns: TableColumnsType<PhysicalActivity> = [
    {
      title: 'Stt',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Tên công việc',
      dataIndex: 'name',
      key: 'name',
      width: 320,
      render: (_, record) =>
        editingRowIds.includes(record.id) || record.id === '' ? (
          <Input
            placeholder='Nhập tên công việc'
            value={
              editingRowIds.includes(record.id)
                ? editedData[record.id]?.name
                : ''
            }
            onChange={(e) =>
              handleInputChange(record.id, 'name', e.target.value)
            }
          />
        ) : (
          record.name
        )
    },
    {
      title: 'Tuổi',
      dataIndex: 'age',
      key: 'age',
      width: 300,
      render: (_, record) =>
        editingRowIds.includes(record.id) || record.id === '' ? (
          <Row gutter={[8, 8]}>
            <Col>
              <InputNumber
                min={0}
                max={100}
                value={
                  editingRowIds.includes(record.id)
                    ? editedData[record.id]?.ageMin
                    : null
                }
                onChange={(value) =>
                  handleInputChange(record.id, 'ageMin', value)
                }
              />
            </Col>
            <Col>
              <InputNumber
                min={0}
                max={100}
                value={
                  editingRowIds.includes(record.id)
                    ? editedData[record.id]?.ageMax
                    : null
                }
                onChange={(value) =>
                  handleInputChange(record.id, 'ageMax', value)
                }
              />
            </Col>
          </Row>
        ) : (
          <Row gutter={[8, 8]}>
            {record.ageMin} - {record.ageMax}
          </Row>
        )
    },
    {
      title: 'Mức độ hoạt động thể lực',
      dataIndex: 'option',
      key: 'option',
      width: 330,
      render: (_, record) => {
        return editingRowIds.includes(record.id) || record.id === '' ? (
          <Select
            placeholder='Chọn'
            value={
              editingRowIds.includes(record.id)
                ? editedData[record.id]?.option?.id
                : record.option?.id
            }
            options={fitnessLevelOptions}
            style={{ width: '100%' }}
            onChange={(value) => {
              const selectedOption = fitnessLevelList.find(
                (opt) => opt.id === value
              );
              handleInputChange(record.id, 'idOptions', selectedOption?.value);
              handleInputChange(record.id, 'option', selectedOption);
            }}
          />
        ) : (
          record?.option?.name
        );
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 140,
      render: (record) => (
        <Row gutter={[16, 16]}>
          {updatable && (
            <Col>
              <Tooltip
                title={
                  editingRowIds.includes(record.id) || record.id === ''
                    ? 'Lưu'
                    : 'Cập nhật'
                }
              >
                <ButtonAnt
                  type='primary'
                  onClick={() =>
                    editingRowIds.includes(record.id) || record.id === ''
                      ? handleSave(record.id)
                      : handleEdit(record)
                  }
                  icon={
                    editingRowIds.includes(record.id) || record.id === '' ? (
                      <SaveOutlined />
                    ) : (
                      <EditOutlined />
                    )
                  }
                  style={{
                    background:
                      editingRowIds.includes(record.id) || record.id === ''
                        ? '#10BA08'
                        : ''
                  }}
                />
              </Tooltip>
            </Col>
          )}
          {deletable && (
            <Col>
              <Tooltip title='Xoá'>
                <ButtonAnt
                  onClick={() => confirmDeleteAdmin(record.id)}
                  type='primary'
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Col>
          )}
          {(editingRowIds.includes(record.id) || record.id === '') && (
            <Col>
              <Tooltip title='Hủy'>
                <ButtonAnt
                  onClick={() => handleCancel(record.id)}
                  type='primary'
                  style={{
                    backgroundColor: '#EBAC0B',
                    borderColor: '#EBAC0B',
                    color: '#fff'
                  }}
                  icon={<CloseCircleOutlined />}
                />
              </Tooltip>
            </Col>
          )}
        </Row>
      )
    }
  ];

  useEffect(() => {
    handlePageChange({});
  }, [handlePageChange]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);

  useBeforeUnload((e) => {
    const isDirty = editingRowIds.length > 0;

    if (isDirty) e.preventDefault();
  });
  return (
    <>
      <Row>
        <Col span={8}>
          {creatable && (
            <Button
              type='dashed'
              icon={<PlusOutlined />}
              iconPosition='start'
              onClick={onAdd}
              css={searchBtnAdd}
              style={{ borderColor: '#1890FF', color: '#1890FF' }}
            >
              Thêm công việc
            </Button>
          )}
        </Col>
      </Row>

      <Table<PhysicalActivity>
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={displayData}
        placeholder='Nhập từ khóa tìm kiếm theo Tên công việc'
        loading={loading}
        totalResult={pagination.totalRows}
        scrollY={tableScrollY(pagination.limitPage)}
        pagination={{
          showQuickJumper: true,
          showSizeChanger: true,
          current: pagination.curPage,
          pageSize: pagination.limitPage,
          total: pagination.totalRows,
          onChange: (page, pageSize) => {
            handlePageChange({ page, pageSize, filter });
          }
        }}
        onFilter={handleSearch}
        onReset={handleReset}
      />
    </>
  );
};
const searchBtnAdd = css`
  margin-top: 2rem;
`;
